import { GridFilterItem } from '@mui/x-data-grid-pro';

import { httpClient, HttpMethod } from './http-client';
import { QueryOption } from '../@types';
import {
  BatchReceive,
  BatchReceiveListResponse,
  IncomingShipmentType,
  NonSerializedShipmentManifestData,
  ReturnSuggestion,
  SerializedShipmentManifestData,
  Shipment,
  ShipmentBatchReceiveData,
  ShipmentByPartData,
  ShipmentListResponse,
  ShipmentManifestSuppliers,
  ShipmentManifestSummery,
  ShipmentServiceAllocationData,
  ShipmentServiceAllocationList,
  ShipmentServiceAllocations,
  ShipmentStatus,
  ShipmentType,
} from '../@types/shipment.type';
import { AddStockManifestRowItemValueType } from '../components/organisms/AddStockManifestRowItem';
import { AddStockManifestDataType } from '../pages/Shipments/AddStock/AddStockManifestFragment';

// Response type for mutations that return the created/updated shipment with ID
interface ShipmentMutationResponse {
  id: number;
  [key: string]: unknown;
}

// Response type for delete operations
interface DeleteManifestResponse {
  id: number;
}

// Response type for service allocation creation
interface ServiceAllocationResponse {
  serviceId: number;
  shipmentId: number;
  id: number;
}

// Response type for individual receive operations
interface IndividualReceiveResponse {
  id: number;
}

// Manifest response for batch receive and individual receive
export interface ShipmentManifestResponse {
  serial?: SerializedShipmentManifestData[];
  nonSerial?: NonSerializedShipmentManifestData[];
}

// Serial values response
interface SerialValuesResponse {
  serials: Array<{
    serial1: string;
    serial2?: string;
    serial3?: string;
  }>;
}

export const createShipment = (shipment: Partial<Shipment>): Promise<Shipment> => {
  return httpClient<Shipment>('/shipment', HttpMethod.Post, shipment);
};

export const updateShipment = (id: number, shipment: Partial<Shipment>): Promise<Shipment> => {
  return httpClient<Shipment>(`/shipment/${id}`, HttpMethod.Put, shipment);
};

export const getShipments = (
  queryOptions: QueryOption,
  excludedColumns?: string
): Promise<ShipmentListResponse> => {
  const { filterModel, page, pageSize, sortModel } = queryOptions;
  let url = `/shipment?page=${page + 1}&limit=${pageSize}&excludedColumns=${excludedColumns}`;

  if (sortModel && sortModel[0] && sortModel[0].field) {
    url += `&order_by=${sortModel[0].field}&order_direction=${sortModel[0].sort}`;
  }

  filterModel.items?.forEach((filter: GridFilterItem) => {
    const field = filter?.field;
    const operator = filter?.operator;
    const value = filter?.value ?? '';
    url += `&${field}=${operator},${value}`;
  });

  return httpClient<ShipmentListResponse>(url, HttpMethod.Get);
};

export const getShipmentById = (id: number): Promise<Shipment> => {
  return httpClient<Shipment>(`/shipment/${id}`, HttpMethod.Get);
};

export const getAllShipmentStatus = (): Promise<ShipmentStatus[]> => {
  return httpClient<ShipmentStatus[]>('/shipmentStatus', HttpMethod.Get);
};

export const getShipmentBatchReceive = (
  queryOptions: QueryOption
): Promise<BatchReceiveListResponse> => {
  const { filterModel, page, pageSize, sortModel } = queryOptions;
  let url = `/shipment/batch/receive?page=${page + 1}&limit=${pageSize}`;

  if (sortModel && sortModel[0] && sortModel[0].field) {
    url += `&order_by=${sortModel[0].field}&order_direction=${sortModel[0].sort}`;
  }

  filterModel.items?.forEach((filter: GridFilterItem) => {
    const field = filter?.field;
    const operator = filter?.operator;
    const value = filter?.value ?? '';
    url += `&${field}=${operator},${value}`;
  });

  return httpClient<BatchReceiveListResponse>(url, HttpMethod.Get);
};

export const getReturnShipmentSuggestions = (
  sourceId: number,
  destinationId: number
): Promise<ReturnSuggestion[]> => {
  return httpClient<ReturnSuggestion[]>(
    `/returnShipmentSuggestions/source/${sourceId}/destination/${destinationId}`,
    HttpMethod.Get
  );
};

export const getAllShipmentTypes = (): Promise<ShipmentType[]> => {
  return httpClient<ShipmentType[]>('/shipmentTypes', HttpMethod.Get);
};

export const getAllShipmentManifestsByShipmentId = (
  shipmentId: number
): Promise<AddStockManifestDataType> => {
  return httpClient<AddStockManifestDataType>(
    `/shipmentManifestList/${shipmentId}/manifestGroup`,
    HttpMethod.Get
  );
};

export const deleteSerializedManifests = (
  manifest: AddStockManifestRowItemValueType
): Promise<DeleteManifestResponse> => {
  return httpClient<DeleteManifestResponse>('/shipmentDeleteManifest', HttpMethod.Put, manifest);
};

export const deleteNonSerializedManifests = (
  shipmentId: number,
  manifestId: number
): Promise<void> => {
  return httpClient<void>(
    `/shipment/${shipmentId}/manifest/nonSerialized/${manifestId}`,
    HttpMethod.Delete
  );
};

export const createAddStockManifestShipment = (
  shipmentId: number,
  addStockManifest: AddStockManifestRowItemValueType[]
): Promise<ShipmentMutationResponse> => {
  return httpClient<ShipmentMutationResponse>(
    `/shipment/${shipmentId}/addstock/manifest/bulk`,
    HttpMethod.Post,
    addStockManifest
  );
};

export const createFreshStockManifestShipment = (
  addStockManifest: AddStockManifestRowItemValueType[]
): Promise<Shipment> => {
  return httpClient<Shipment>('/createFreshAddStockManifest', HttpMethod.Post, addStockManifest);
};

export const createNonSerializedManifestShipment = (
  shipmentId: number,
  nonSerializedManifest: AddStockManifestRowItemValueType[]
): Promise<ShipmentMutationResponse> => {
  return httpClient<ShipmentMutationResponse>(
    `/shipment/${shipmentId}/nonSerialized/manifest/bulk`,
    HttpMethod.Post,
    nonSerializedManifest
  );
};

export const getSerialValues = (
  serialisedManifestDataItemValue: AddStockManifestRowItemValueType
): Promise<SerialValuesResponse> => {
  return httpClient<SerialValuesResponse>(
    '/getSerialValues',
    HttpMethod.Post,
    serialisedManifestDataItemValue
  );
};

export const deleteSerializedManifestById = (id: number): Promise<void> => {
  return httpClient<void>(`/shipment/manifest/serialized/${id}`, HttpMethod.Delete);
};

export const getAllShipmentManifest = (
  partTypeId: number,
  nodeId: number
): Promise<ShipmentManifestResponse> => {
  return httpClient<ShipmentManifestResponse>(
    `/shipments/manifest/partTypes/${partTypeId}/node/${nodeId}`,
    HttpMethod.Get
  );
};

export const getShipmentBatchReceiveById = (batchId: number): Promise<BatchReceive> => {
  return httpClient<BatchReceive>(`/shipments/batch/${batchId}/receive`, HttpMethod.Get);
};

export const createShipmentBatchReceive = (
  batchReceive: ShipmentBatchReceiveData
): Promise<BatchReceive> => {
  return httpClient<BatchReceive>('/shipments/batch/receive', HttpMethod.Post, batchReceive);
};

export const getAllManifestByShipment = (shipmentId: number): Promise<ShipmentManifestResponse> => {
  return httpClient<ShipmentManifestResponse>(`/shipments/${shipmentId}/manifest`, HttpMethod.Get);
};

export const createShipmentIndividualReceive = (
  shipmentId: number,
  receive: ShipmentBatchReceiveData
): Promise<IndividualReceiveResponse> => {
  return httpClient<IndividualReceiveResponse>(
    `/shipments/${shipmentId}/individual/receive`,
    HttpMethod.Post,
    receive
  );
};

export const bulkCreateShipmentIndividualReceive = (
  shipmentId: number,
  receive: ShipmentBatchReceiveData
): Promise<IndividualReceiveResponse> => {
  return httpClient<IndividualReceiveResponse>(
    `/shipments/${shipmentId}/individual/receive/bulk`,
    HttpMethod.Post,
    receive
  );
};

export const getShipmentServiceAllocationList = (
  serviceId: number
): Promise<ShipmentServiceAllocationList[]> => {
  return httpClient<ShipmentServiceAllocationList[]>(
    `/shipmentServiceAllocation/service/${serviceId}`,
    HttpMethod.Get
  );
};

export const createShipmentServiceAllocation = (
  shipmentServiceAllocation: ShipmentServiceAllocationData
): Promise<ServiceAllocationResponse> => {
  return httpClient<ServiceAllocationResponse>(
    '/shipmentServiceAllocation',
    HttpMethod.Post,
    shipmentServiceAllocation
  );
};

export const deleteShipmentServiceAllocation = (id: number): Promise<void> => {
  return httpClient<void>(`/shipmentServiceAllocation/${id}`, HttpMethod.Delete);
};

export const getIncomingShipments = (shipmentStatus: string[]): Promise<IncomingShipmentType[]> => {
  return httpClient<IncomingShipmentType[]>(
    `/shipmentsByShipmentStatus?shipmentStatus=${shipmentStatus}`,
    HttpMethod.Get
  );
};

export const getShipmentServiceAllocationsByShipmentId = (
  shipmentId: number
): Promise<ShipmentServiceAllocations[]> => {
  return httpClient<ShipmentServiceAllocations[]>(
    `/shipmentServiceAllocation/shipment/${shipmentId}`,
    HttpMethod.Get
  );
};

export const getAddStockShipmentManifestPricing = (
  partTypeId: number
): Promise<ShipmentManifestSuppliers[]> => {
  return httpClient<ShipmentManifestSuppliers[]>(
    `/shipmentManifest/${partTypeId}/supplierPricing`,
    HttpMethod.Get
  );
};

export const getShipmentDetailsPartIncludes = (
  serviceId: number,
  partIds: number[]
): Promise<ShipmentManifestSummery[]> => {
  return httpClient<ShipmentManifestSummery[]>(
    `/getShipmentDetailsPartIncludesForPartAction/service/${serviceId}`,
    HttpMethod.Post,
    { partIds }
  );
};

export const getLatestShipmentByPartId = (partId: number): Promise<ShipmentByPartData> => {
  return httpClient<ShipmentByPartData>(`/latestShipmentsByPartId/${partId}`, HttpMethod.Get);
};
